import { useEffect, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { AffiliationStatus } from "@api/shared-runtime/enums";
import type {
  RosterIdentityConflictCause,
  RosterNameMatch,
} from "@api/shared-runtime/roster-provider-match";
import { toast } from "sonner";
import { useOrganization } from "@/hooks/use-organization";
import { useTRPC } from "@/trpc";
import {
  EMPTY_PROVIDER_FORM_VALUES as DEFAULT_VALUES,
  providerFormSchema,
  toSubmittedPhone,
  type ProviderFormValues,
} from "./provider-form-schema";
import {
  isRosterNameMatchError,
  parseRosterIdentityConflict,
} from "./roster-match/roster-error-cause";

/** Form fields that identify a provider; editing one invalidates a match check. */
const IDENTITY_FIELDS: string[] = ["firstName", "lastName", "npi", "email"];

type UseInviteProviderFormInput = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSaved?: (organizationBusinessUnitId: string) => void | Promise<void>;
  requireEmploymentStatus?: boolean;
};

type RosterProviderCheckInput = {
  email?: string | null;
  npi?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  sendInvitation?: boolean;
};

export type NewProviderEmploymentStatus =
  | AffiliationStatus.Candidate
  | AffiliationStatus.Hired;

export const useInviteProviderForm = ({
  open,
  onOpenChange,
  onSaved,
  requireEmploymentStatus = false,
}: UseInviteProviderFormInput) => {
  const { selectedOrgBusinessUnit } = useOrganization();
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const [manualEntry, setManualEntry] = useState(false);
  const [employmentStatus, setEmploymentStatus] = useState<
    NewProviderEmploymentStatus | undefined
  >();
  const [employmentError, setEmploymentError] = useState<string | null>(null);
  const [identityConflict, setIdentityConflict] =
    useState<RosterIdentityConflictCause | null>(null);
  const [nameMatches, setNameMatches] = useState<RosterNameMatch[]>([]);
  const form = useForm<ProviderFormValues>({
    resolver: zodResolver(providerFormSchema),
    defaultValues: DEFAULT_VALUES,
  });
  // The dialog stays mounted for the whole page, so this would otherwise fetch
  // the full professional-type list on every visit to the invitations route.
  const { data: professionalTypes } = useQuery({
    ...trpc.healthcareProfessionalTypes.getAll.queryOptions(),
    enabled: open,
  });

  useEffect(() => {
    if (!open) {
      return;
    }
    form.reset(DEFAULT_VALUES);
    setManualEntry(false);
    setEmploymentStatus(undefined);
    setEmploymentError(null);
    setIdentityConflict(null);
    setNameMatches([]);
  }, [open, form]);

  useEffect(() => {
    const subscription = form.watch((_values, { name }) => {
      if (name && IDENTITY_FIELDS.includes(name)) {
        setIdentityConflict(null);
        setNameMatches([]);
      }
    });
    return () => subscription.unsubscribe();
  }, [form]);

  const providerNpi = form.watch("providerNpi");
  useEffect(() => {
    if (manualEntry) {
      return;
    }
    if (!providerNpi) {
      form.setValue("npi", "", { shouldValidate: false });
      form.setValue("firstName", "", { shouldValidate: false });
      form.setValue("lastName", "", { shouldValidate: false });
      form.setValue("specialtyCodes", [], { shouldValidate: false });
      form.setValue("healthcareProfessionalType", undefined, {
        shouldValidate: false,
      });
      return;
    }

    form.setValue("npi", providerNpi.npi, { shouldValidate: true });
    form.setValue("firstName", providerNpi.firstName, {
      shouldValidate: false,
    });
    form.setValue("lastName", providerNpi.lastName, { shouldValidate: false });
    form.setValue(
      "specialtyCodes",
      providerNpi.specialties
        .map((specialty) => specialty.code)
        .filter(Boolean),
      { shouldValidate: false },
    );
    const matchedType = providerNpi.providerTypeId
      ? professionalTypes?.find(
          (type) => type.id === providerNpi.providerTypeId,
        )
      : undefined;
    form.setValue(
      "healthcareProfessionalType",
      matchedType ? { id: matchedType.id, name: matchedType.name } : undefined,
      { shouldValidate: false },
    );
  }, [providerNpi, professionalTypes, form, manualEntry]);

  const checkProviders = useMutation(
    trpc.organizationBusinessUnitAffiliation.checkRosterProviders.mutationOptions(),
  );

  // Looks up the matches behind a name-match rejection. A definite duplicate
  // found here (someone added it meanwhile) is shown like an identity conflict.
  const showMatchesFor = async (
    provider: RosterProviderCheckInput,
    message: string,
  ) => {
    try {
      const [check] = await checkProviders.mutateAsync({
        organizationBusinessUnitId: selectedOrgBusinessUnit.id,
        providers: [provider],
      });
      setIdentityConflict(check?.identityConflict ?? null);
      setNameMatches(check?.identityConflict ? [] : (check?.nameMatches ?? []));
      form.setError("root", {
        message: check?.identityConflict?.message ?? message,
      });
    } catch (error) {
      form.setError("root", {
        message:
          error instanceof Error && error.message
            ? error.message
            : "Failed to check for matching providers.",
      });
    }
  };

  const addProvider = useMutation(
    trpc.organizationBusinessUnitAffiliation.createRosterProvider.mutationOptions(
      {
        onSuccess: async (result, variables) => {
          await onSaved?.(variables.organizationBusinessUnitId);
          await Promise.all([
            queryClient.invalidateQueries({
              queryKey:
                trpc.organizationBusinessUnitAffiliation.listInvitations.queryKey(
                  {
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                  },
                ),
            }),
            queryClient.invalidateQueries({
              queryKey:
                trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryKey(
                  {
                    organizationBusinessUnitId: selectedOrgBusinessUnit.id,
                  },
                ),
            }),
          ]);
          // Keyed off `invitationSent`, not the row's status: an invitation
          // already pending reads as "invited" while no new email was queued,
          // and claiming one was sent would be a lie.
          toast.success(
            result.invitationSent
              ? `Provider added and invitation sent to ${result.invitation?.email ?? result.provider.email}.`
              : "Provider added. No invitation was sent.",
          );
          form.reset(DEFAULT_VALUES);
          onOpenChange(false);
        },
        // Shown in the dialog's form-level slot so the reason stays beside
        // the form; the next submit clears it.
        onError: (error, variables) => {
          const message = error.message || "Failed to add provider.";
          setIdentityConflict(parseRosterIdentityConflict(error));
          setNameMatches([]);
          form.setError("root", { message });
          if (isRosterNameMatchError(error)) {
            void showMatchesFor(
              {
                email: variables.email,
                npi: variables.npi,
                firstName: variables.firstName,
                lastName: variables.lastName,
                sendInvitation: variables.sendInvitation,
              },
              message,
            );
          }
        },
      },
    ),
  );

  const reinstateProvider = useMutation(
    trpc.organizationBusinessUnitAffiliation.reinstate.mutationOptions({
      onSuccess: async (result, variables) => {
        await onSaved?.(variables.organizationBusinessUnitId);
        await Promise.all([
          queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listInvitations.queryKey(
                { organizationBusinessUnitId: selectedOrgBusinessUnit.id },
              ),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listForBusinessUnit.queryKey(
                { organizationBusinessUnitId: selectedOrgBusinessUnit.id },
              ),
          }),
          queryClient.invalidateQueries({
            queryKey:
              trpc.organizationBusinessUnitAffiliation.listEnded.pathKey(),
          }),
          queryClient.invalidateQueries({
            queryKey: trpc.profile.list.queryKey(),
          }),
          queryClient.invalidateQueries({ queryKey: ["profileListInfinite"] }),
        ]);
        toast.success("Provider reinstated.");
        form.reset(DEFAULT_VALUES);
        onOpenChange(false);
      },
      onError: (error) => {
        form.setError("root", {
          message: error.message || "Failed to reinstate provider.",
        });
      },
    }),
  );

  const reinstateEndedProvider = (endedAffiliationId: string) => {
    reinstateProvider.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      endedAffiliationId,
    });
  };

  const submit = (values: ProviderFormValues) => {
    if (requireEmploymentStatus && !employmentStatus) {
      setEmploymentError("Choose Candidate or Hired.");
      return;
    }
    setIdentityConflict(null);
    addProvider.mutate({
      organizationBusinessUnitId: selectedOrgBusinessUnit.id,
      email: values.email || undefined,
      npi: values.npi || undefined,
      firstName: values.firstName || undefined,
      lastName: values.lastName || undefined,
      phone: toSubmittedPhone(values.phone) ?? undefined,
      healthcareProfessionalTypeId: values.healthcareProfessionalType?.id,
      specialtyCodes: values.specialtyCodes,
      sendInvitation: values.sendInvitation,
      ...(nameMatches.length > 0
        ? { reviewedNameMatchIds: nameMatches.map((match) => match.id) }
        : {}),
      ...(requireEmploymentStatus && employmentStatus
        ? { employmentStatus }
        : {}),
    });
  };

  return {
    form,
    manualEntry,
    setManualEntry,
    providerNpi,
    addProvider,
    identityConflict,
    nameMatches,
    isCheckingMatches: checkProviders.isPending,
    reinstateProvider,
    reinstateEndedProvider,
    submit,
    employmentStatus,
    employmentError,
    chooseEmploymentStatus: (status: NewProviderEmploymentStatus) => {
      setEmploymentStatus(status);
      setEmploymentError(null);
    },
  };
};
